import { getSocket, SOCKET_URL } from '../socket';
import { getUserId } from '../identity';

// The site's own visitor counts and connection checks, for the admin
// dashboard. One small beacon per page shown, one more when it is left (time
// spent and how far it was scrolled), and a ping over the game connection
// every 30 seconds to measure the round trip. Nothing is sent from the
// prerenderer or other automated browsers.

const ENDPOINT = `${SOCKET_URL.replace(/\/$/, '')}/sequenceApi/t`;
const SESSION_IDLE_MS = 30 * 60 * 1000;
const PING_MS = 30 * 1000;

const automated = () => typeof navigator !== 'undefined' && navigator.webdriver;

const store = (kind) => {
    try { return kind === 'local' ? window.localStorage : window.sessionStorage; } catch (error) { return null; }
};
const randomId = (n) => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    const bytes = new Uint8Array(n);
    (window.crypto || window.msCrypto).getRandomValues(bytes);
    return [...bytes].map((b) => chars[b % chars.length]).join('');
};

/** Game ids and replay ids become one page each, so the reports stay readable. */
export const pageName = (path) => {
    if (/^\/r\/[^/]+/.test(path)) return '/r/:game';
    if (/^\/classic\/[^/]+/.test(path)) return '/classic/:game';
    if (/^\/[a-f0-9]{24}\/?$/i.test(path)) return '/:game';
    return path.length > 1 ? path.replace(/\/$/, '') : '/';
};

const send = (body) => {
    const data = JSON.stringify(body);
    try {
        if (navigator.sendBeacon && navigator.sendBeacon(ENDPOINT, new Blob([data], { type: 'text/plain' }))) return;
    } catch (error) { /* fall through */ }
    try {
        fetch(ENDPOINT, { method: 'POST', body: data, keepalive: true, mode: 'no-cors', headers: { 'Content-Type': 'text/plain' } }).catch(() => {});
    } catch (error) { /* tracking never matters */ }
};

/** This visit: a new one after 30 minutes away. */
const session = () => {
    const s = store('session');
    const now = Date.now();
    let sid = s?.getItem('stats:sid');
    const last = Number(s?.getItem('stats:last') || 0);
    const isNew = !sid || now - last > SESSION_IDLE_MS;
    if (isNew) sid = randomId(12);
    s?.setItem('stats:sid', sid);
    s?.setItem('stats:last', String(now));
    return { sid, isNew };
};

let current = null; // { id, shownAt, visibleMs, visibleSince, scroll }

const scrolled = () => {
    const doc = document.documentElement;
    const max = Math.max(1, doc.scrollHeight - window.innerHeight);
    return Math.min(100, Math.round(((window.scrollY || 0) / max) * 100));
};

const visibleMs = (view) => view.visibleMs + (view.visibleSince ? Date.now() - view.visibleSince : 0);

const leave = () => {
    if (!current) return;
    send({ k: 'leave', id: current.id, ms: visibleMs(current), scroll: current.scroll });
};

/** Called on every route change. */
export const trackPage = (path) => {
    if (automated()) return;
    try {
        leave();
        const { sid, isNew } = session();
        const local = store('local');
        const first = !local?.getItem('stats:seen');
        local?.setItem('stats:seen', '1');
        const params = new URLSearchParams(window.location.search);
        const utm = isNew && (params.get('utm_source') || params.get('utm_medium') || params.get('utm_campaign'))
            ? { source: params.get('utm_source'), medium: params.get('utm_medium'), campaign: params.get('utm_campaign') } : null;
        current = { id: randomId(16), visibleMs: 0, visibleSince: document.hidden ? null : Date.now(), scroll: 0 };
        send({
            id: current.id,
            v: getUserId(),
            sid,
            p: pageName(path),
            first,
            r: isNew ? document.referrer : '',
            utm,
            w: window.innerWidth,
            tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
            lang: navigator.language,
        });
    } catch (error) { /* tracking never matters */ }
};

let started = false;
let lastRtt = null;
let lastPage = '/';

const ping = () => {
    const socket = getSocket();
    if (!socket.connected || document.hidden) return;
    const net = navigator.connection;
    const began = performance.now();
    socket.timeout(10000).emit('sys:ping', {
        rtt: lastRtt,
        page: lastPage,
        visitor: getUserId(),
        net: net ? { type: net.effectiveType, downlink: net.downlink, rtt: net.rtt } : null,
    }, (error) => {
        if (!error) lastRtt = Math.round(performance.now() - began);
    });
};

/** Starts the visibility bookkeeping and the ping. Once per page load. */
export const startSiteStats = () => {
    if (started || automated()) return;
    started = true;
    document.addEventListener('visibilitychange', () => {
        if (!current) return;
        if (document.hidden) {
            current.visibleMs = visibleMs(current);
            current.visibleSince = null;
            leave();
        } else {
            current.visibleSince = Date.now();
            session();
        }
    });
    window.addEventListener('pagehide', leave);
    window.addEventListener('scroll', () => {
        if (current) current.scroll = Math.max(current.scroll, scrolled());
    }, { passive: true });
    const socket = getSocket();
    // Twice right after connecting, so the first report already has a round trip.
    socket.on('connect', () => { setTimeout(ping, 1500); setTimeout(ping, 4000); });
    if (socket.connected) { setTimeout(ping, 1500); setTimeout(ping, 4000); }
    setInterval(ping, PING_MS);
};

export const setStatsPage = (path) => { lastPage = pageName(path); };
